import type { Rarity } from "@/domain/gems-game/domain/lib/Earning";
import type { UserStatsResponse } from "@/domain/gems-game/domain/lib/Sweepstake";
import { useAccessToken } from "@/hooks";
import { authorizedFetcherGet } from "@/utils/request";
import { RARITY_LEVELS } from "@/utils/rewards";
import { onDarkBase } from "@biom3/design-tokens";
import { Body } from "@biom3/react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import useSWRImmutable from "swr";
import { SEGMENT_LENGTHS } from "./KeyProgressBar";
import { KeyProgressBar } from "./KeyProgressBar";
import { RarityKeyIcon } from "./RarityKeyIcon";
import { RarityText } from "./RarityText";
import { WeeklyRewardsInfoDropdown } from "./WeeklyRewardsInfoDropdown";

export const HeaderProgressBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const getAccessToken = useAccessToken();

  const { data } = useSWRImmutable<UserStatsResponse>(
    "/v2/rewards/sweepstakes/user-stats/predicted",
    (path: string) =>
      authorizedFetcherGet<UserStatsResponse>(path, getAccessToken),
  );

  const tierIndex = useMemo(() => {
    const idx = RARITY_LEVELS.indexOf(
      (data?.userStats?.bucketName || "Common") as Rarity,
    );
    return idx === -1 ? 0 : idx;
  }, [data]);

  const progressPercentage = useMemo(() => {
    if (typeof data?.userStats?.progressPercentage === "number") {
      return Math.max(0, Math.min(100, data.userStats.progressPercentage));
    }
    return 0;
  }, [data]);

  const currentRarity: Rarity = RARITY_LEVELS[tierIndex];

  const cumulativeBefore = RARITY_LEVELS.slice(0, tierIndex).reduce(
    (sum, r) => sum + SEGMENT_LENGTHS[r],
    0,
  );
  const absoluteProgress = Math.min(
    100,
    cumulativeBefore +
      (progressPercentage / 100) * SEGMENT_LENGTHS[currentRarity],
  );

  const [anchorPosition, setAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const totalQuestsCompleted = useMemo(() => {
    return data?.targetQuests?.filter((q) => q.completed).length;
  }, [data]);

  const handleBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (anchorPosition) {
      setAnchorPosition(null);
    } else {
      setAnchorPosition({
        top: e.clientY,
        left: e.clientX,
      });
    }
  };

  const handleClose = () => setAnchorPosition(null);

  return (
    <Box
      width="100%"
      maxWidth="1200px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={onDarkBase.spacing.x1}
      height={isMobile ? 60 : 78}
    >
      <WeeklyRewardsInfoDropdown
        userStats={data}
        currentRarity={currentRarity}
        anchorPosition={anchorPosition}
        open={Boolean(anchorPosition)}
        onCloseExtern={handleClose}
      />

      <Box
        position="relative"
        width="100%"
        onClick={handleBarClick}
        sx={{
          cursor: "pointer",
          borderRadius: "18px",
          border: `${isMobile ? "8px" : "14px"} solid ${onDarkBase.color.neutral[500]}`,
          zIndex: 1,
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <KeyProgressBar />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: isMobile
              ? "translate(-50%, -60%)"
              : "translate(-50%, -90%)",
            px: onDarkBase.spacing.x4,
            py: onDarkBase.spacing.x2,
            backgroundColor: onDarkBase.color.neutral[500],
            clipPath:
              "polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%)",
            whiteSpace: "nowrap",
          }}
        >
          <Body
            size={isMobile ? "xxSmall" : "xSmall"}
            sx={{
              position: "relative",
              top: isMobile ? "-13px" : "-6px",
              fontWeight: "bold",
            }}
          >
            NEXT DRAW PROJECTION /{" "}
            <RarityText
              rarity={currentRarity}
              sx={{ textTransform: "uppercase" }}
            />
          </Body>
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: isMobile
              ? "translate(-50%, 60%)"
              : "translate(-50%, 85%)",
            px: onDarkBase.spacing.x6,
            py: onDarkBase.spacing.x2,
            backgroundColor: onDarkBase.color.neutral[500],
            clipPath:
              "polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%)",
            whiteSpace: "nowrap",
          }}
        >
          <Body
            size={isMobile ? "xxSmall" : "xSmall"}
            sx={{
              position: "relative",
              top: isMobile ? "10px" : "6px",
              fontWeight: "bold",
            }}
          >
            {totalQuestsCompleted ?? 0}{" "}
            {totalQuestsCompleted === 1 ? "QUEST" : "QUESTS"} COMPLETED
          </Body>
        </Box>

        {data?.userStats && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: `${absoluteProgress}%`,
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              zIndex: 4,
            }}
          >
            <RarityKeyIcon rarity={currentRarity} size={isMobile ? 22 : 32} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
